/**
 * 결제 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Modal, Alert, Form } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { paymentService } from '../../services/paymentService';
import { Payment, PaymentStatus, PaymentMethod } from '../../types/payment';
import { format } from 'date-fns';

const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');

  const { data: payment, isLoading, error } = useQuery({
    queryKey: ['payment', id],
    queryFn: () => paymentService.getPayment(Number(id)),
    enabled: !!id,
  });

  const refundMutation = useMutation({
    mutationFn: (reason: string) => paymentService.refundPayment(Number(id), reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment', id] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payment-statistics'] });
      setShowRefundModal(false);
      setRefundReason('');
    },
  });

  const getStatusBadge = (status: PaymentStatus) => {
    const statusMap: Record<PaymentStatus, { variant: string; label: string }> = {
      pending: { variant: 'warning', label: '대기중' },
      completed: { variant: 'success', label: '완료' },
      failed: { variant: 'danger', label: '실패' },
      refunded: { variant: 'info', label: '환불됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getMethodLabel = (method: PaymentMethod) => {
    const methodMap: Record<PaymentMethod, string> = {
      card: '카드',
      qrcode: 'QR코드',
    };
    return methodMap[method] || method;
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <p style={{ color: '#fff' }}>로딩 중...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !payment) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <Alert variant="danger">결제 정보를 불러올 수 없습니다.</Alert>
          <Button variant="secondary" onClick={() => navigate('/admin/payments')}>
            목록으로
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const canRefund = payment.status === 'completed' && !payment.refunded_at;

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>결제 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/payments')} className="me-2">
              목록으로
            </Button>
            {canRefund && (
              <Button variant="danger" onClick={() => setShowRefundModal(true)}>
                환불 처리
              </Button>
            )}
          </div>
        </div>

        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">결제 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">결제번호</label>
                      <div className="detail-value">{payment.payment_number}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상태</label>
                      <div className="detail-value">{getStatusBadge(payment.status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">결제방법</label>
                      <div className="detail-value">
                        {getMethodLabel(payment.payment_method)}
                        {payment.payment_method_detail && (
                          <small className="text-muted d-block">{payment.payment_method_detail}</small>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">결제금액</label>
                      <div className="detail-value" style={{ fontSize: '20px', fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
                        {formatCurrency(payment.amount)}
                      </div>
                    </div>
                  </Col>
                  {payment.paid_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">결제일시</label>
                        <div className="detail-value">
                          {format(new Date(payment.paid_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                        </div>
                      </div>
                    </Col>
                  )}
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">생성일시</label>
                      <div className="detail-value">
                        {format(new Date(payment.created_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                      </div>
                    </div>
                  </Col>
                  {payment.pg_transaction_id && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">PG 거래번호</label>
                        <div className="detail-value">{payment.pg_transaction_id}</div>
                      </div>
                    </Col>
                  )}
                  {payment.refunded_at && (
                    <>
                      <Col md={6}>
                        <div className="detail-item mb-3">
                          <label className="detail-label">환불일시</label>
                          <div className="detail-value">
                            {format(new Date(payment.refunded_at), 'yyyy년 MM월 dd일 HH:mm:ss')}
                          </div>
                        </div>
                      </Col>
                      {payment.refund_reason && (
                        <Col md={12}>
                          <div className="detail-item mb-3">
                            <label className="detail-label">환불 사유</label>
                            <div className="detail-value">{payment.refund_reason}</div>
                          </div>
                        </Col>
                      )}
                    </>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">예약 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약번호</label>
                      <div className="detail-value">
                        {payment.booking?.booking_number || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">방문일</label>
                      <div className="detail-value">
                        {payment.booking?.visit_date 
                          ? format(new Date(payment.booking.visit_date), 'yyyy년 MM월 dd일')
                          : '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약 금액</label>
                      <div className="detail-value">
                        {payment.booking?.final_amount 
                          ? formatCurrency(payment.booking.final_amount)
                          : '-'}
                      </div>
                    </div>
                  </Col>
                </Row>
                {payment.booking && (
                  <Button
                    variant="link"
                    onClick={() => navigate(`/admin/bookings/${payment.booking_id}`)}
                    style={{ padding: 0, color: 'var(--color-accent-cyan)' }}
                  >
                    예약 상세 보기 →
                  </Button>
                )}
              </Card.Body>
            </Card>

            {payment.pg_response_data && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">PG사 응답 데이터</h5>
                </Card.Header>
                <Card.Body>
                  <pre style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                    padding: '12px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: 'var(--color-text-secondary)',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(payment.pg_response_data, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col md={4}>
            {payment.pg_provider && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">PG사 정보</h5>
                </Card.Header>
                <Card.Body>
                  <div className="detail-item mb-3">
                    <label className="detail-label">PG사명</label>
                    <div className="detail-value">{payment.pg_provider.name}</div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* 환불 모달 */}
        <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>환불 처리</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              정말로 이 결제를 환불하시겠습니까?
              <div className="mt-2">
                <strong>결제번호:</strong> {payment.payment_number}<br />
                <strong>결제금액:</strong> {formatCurrency(payment.amount)}
              </div>
            </Alert>
            <Form.Group className="mt-3">
              <Form.Label>환불 사유</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="환불 사유를 입력하세요"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
              닫기
            </Button>
            <Button
              variant="danger"
              onClick={() => refundMutation.mutate(refundReason)}
              disabled={refundMutation.isPending || !refundReason.trim()}
            >
              {refundMutation.isPending ? '처리 중...' : '환불 처리'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .detail-item {
          padding: 8px 0;
        }
        .detail-label {
          display: block;
          font-size: 12px;
          color: var(--color-text-secondary);
          margin-bottom: 4px;
          font-weight: 500;
        }
        .detail-value {
          font-size: 14px;
          color: var(--color-text-primary);
        }
      `}</style>
    </AdminLayout>
  );
};

export default PaymentDetail;

